/**
 * <s-name-preview> — live preview of an embroidery personalisation.
 *
 * Mirrors the typed name, chosen font and thread colour into a preview box.
 * Markup contract (inside the element):
 *   [data-preview-box]     hidden until JS runs, so no-JS buyers never see a stale box
 *   [data-preview-text]    receives the name; data-placeholder holds the empty-state text
 *   [data-preview-name]    the name input
 *   input[data-preview-class]  font radios; value of data-preview-class is applied to the text
 *   [data-preview-color]   thread colour swatch group (checked radio has data-hex) or a select matched by keyword
 * The element re-runs on connect, so it keeps working when the product-form partial re-renders.
 */
const THREAD_COLOURS = [
  ['pauwblauw', '#0F6E8C'],
  ['licht blauw', '#9CC3DE'],
  ['lichtblauw', '#9CC3DE'],
  ['donkerblauw', '#1F3A5F'],
  ['blauw', '#4F7DB3'],
  ['licht roze', '#EFB3C3'],
  ['lichtroze', '#EFB3C3'],
  ['oud roze', '#C98E95'],
  ['roze', '#E58FAA'],
  ['bruin', '#6B4423'],
  ['camel', '#B07A4B'],
  ['beige', '#CDB79E'],
  ['wit', '#FFFFFF'],
  ['creme', '#F3EAD8'],
  ['zwart', '#1E1E1E'],
  ['grijs', '#8C8C8C'],
  ['goud', '#C9A24B'],
  ['geel', '#E8C547'],
  ['oker', '#C99A2E'],
  ['salie', '#8FA58C'],
  ['groen', '#5E8B5A'],
  ['mint', '#A8D5BA'],
  ['rood', '#B23A3A'],
  ['lila', '#B9A3D0'],
  ['paars', '#7A4E9E'],
];

const LIGHT_THREADS = ['#FFFFFF', '#F3EAD8', '#EFB3C3', '#9CC3DE', '#A8D5BA', '#CDB79E', '#E8C547'];

class SNamePreview extends HTMLElement {
  #ac = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.addEventListener('input', () => this.#update(), { signal });
    this.addEventListener('change', () => this.#update(), { signal });

    const box = this.querySelector('[data-preview-box]');
    if (box) box.hidden = false;
    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #update() {
    const text = this.querySelector('[data-preview-text]');
    if (!text) return;

    const nameInput = this.querySelector('[data-preview-name]');
    const typed = nameInput instanceof HTMLInputElement ? nameInput.value.trim() : '';
    text.textContent = typed || text.dataset.placeholder || '';
    text.classList.toggle('opacity-50', !typed);

    // Swap the font class: remove any previous preview font class, then add the checked one.
    for (const radio of this.querySelectorAll('input[data-preview-class]')) {
      text.classList.remove(radio.dataset.previewClass);
    }
    const checked = this.querySelector('input[data-preview-class]:checked');
    if (checked) text.classList.add(checked.dataset.previewClass);

    this.#applyColour(text, this.querySelector('[data-preview-color]'), 'text');

    // Blankets: the two stars follow their own colour select.
    const stars = this.querySelector('[data-preview-stars]');
    if (stars) this.#applyColour(stars, this.querySelector('[data-preview-stars-color]'), 'svg');
  }

  #applyColour(element, group, mode) {
    // The group is a swatch fieldset (checked radio carries data-hex) or a legacy select.
    let hex = '';
    if (group instanceof HTMLSelectElement) {
      const colourName = group.value.toLowerCase();
      const match = THREAD_COLOURS.find(([keyword]) => colourName.includes(keyword));
      hex = match ? match[1] : '';
    } else if (group) {
      const checked = group.querySelector('input[type="radio"]:checked');
      hex = checked instanceof HTMLInputElement ? checked.dataset.hex || '' : '';
    }
    element.style.color = hex;
    // Light threads get a soft outline so they stay readable on the Teddy background.
    const light = hex && (LIGHT_THREADS.includes(hex.toUpperCase()) || this.#isLight(hex));
    const shadow = light ? '0 1px 2px rgb(46 31 25 / 0.45)' : '';
    if (mode === 'svg') {
      element.style.filter = light ? 'drop-shadow(0 1px 1px rgb(46 31 25 / 0.45))' : '';
    } else {
      element.style.textShadow = shadow;
    }
  }

  #isLight(hex) {
    const value = hex.replace('#', '');
    if (value.length !== 6) return false;
    const r = Number.parseInt(value.slice(0, 2), 16);
    const g = Number.parseInt(value.slice(2, 4), 16);
    const b = Number.parseInt(value.slice(4, 6), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.7;
  }
}

if (!customElements.get('s-name-preview')) {
  customElements.define('s-name-preview', SNamePreview);
}
